import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowUp } from "lucide-react";
import Seo from "@/components/Seo";

/* ---------------- Markdown-lite parser ----------------
   Supported syntax:
     ## Heading  → section heading (also emits an anchor for TOC)
     ### Sub     → sub heading
     > quote     → blockquote
     • / - / *   → bullet list (grouped across blank lines)
     **bold**    → strong
     *italic*    → em
   Regular paragraphs are separated by blank lines.
--------------------------------------------------------- */
function slugifyHeading(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 60) || "section";
}

function parseContent(md) {
  const blocks = [];
  const lines = md.split(/\r?\n/);
  let i = 0;
  while (i < lines.length) {
    const raw = lines[i];
    const trimmed = raw.trim();

    if (!trimmed) { i++; continue; }

    if (trimmed.startsWith("## ")) {
      const text = trimmed.slice(3).trim();
      blocks.push({ type: "h2", text, id: slugifyHeading(text) });
      i++; continue;
    }
    if (trimmed.startsWith("### ")) {
      const text = trimmed.slice(4).trim();
      blocks.push({ type: "h3", text });
      i++; continue;
    }
    if (trimmed.startsWith("> ")) {
      const buf = [];
      while (i < lines.length && lines[i].trim().startsWith("> ")) {
        buf.push(lines[i].trim().slice(2));
        i++;
      }
      blocks.push({ type: "quote", text: buf.join(" ") });
      continue;
    }
    if (/^[•\-\*]\s/.test(trimmed)) {
      const items = [];
      // Collect all consecutive bullet lines even across blank lines
      while (i < lines.length) {
        const t = lines[i].trim();
        if (/^[•\-\*]\s/.test(t)) {
          items.push(t.replace(/^[•\-\*]\s/, ""));
          i++;
        } else if (!t) {
          // Peek: if next non-blank is another bullet, continue
          let j = i + 1;
          while (j < lines.length && !lines[j].trim()) j++;
          if (j < lines.length && /^[•\-\*]\s/.test(lines[j].trim())) {
            i = j;
          } else {
            break;
          }
        } else {
          break;
        }
      }
      blocks.push({ type: "list", items });
      continue;
    }

    // Paragraph — collect until blank line or special line
    const paraLines = [raw];
    i++;
    while (i < lines.length) {
      const n = lines[i];
      const nt = n.trim();
      if (!nt) break;
      if (/^(#{2,3}\s|[•\-\*]\s|>\s)/.test(nt)) break;
      paraLines.push(n);
      i++;
    }
    blocks.push({ type: "p", text: paraLines.join(" ").trim() });
  }
  return blocks;
}

function renderInline(text) {
  // Bold **x** and italic *x* — bold first to avoid conflict
  const nodes = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith("**")) {
      nodes.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else {
      nodes.push(<em key={key++}>{token.slice(1, -1)}</em>);
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

/* ---------------- Banner fallback ---------------- */
function TypographicBanner({ post }) {
  const fragment = post.title.split(" ").slice(0, 3).join(" ");
  return (
    <div className="relative aspect-[16/7] overflow-hidden rounded-sm border border-border grain-overlay">
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/85 to-primary/70" />
      {/* Diagonal warm accent band */}
      <div className="absolute -right-16 -top-16 w-96 h-96 rounded-full bg-accent/25 blur-3xl" />
      <div className="absolute inset-0 flex flex-col justify-between p-8 md:p-14 text-primary-foreground">
        <div className="flex items-start justify-between gap-6">
          <div className="text-[10px] uppercase tracking-[0.3em] opacity-70">
            Essay · {post.category}
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] opacity-70">
            {post.read_minutes} min read
          </div>
        </div>
        <div>
          <div className="font-serif text-4xl md:text-6xl italic leading-[1] tracking-tighter opacity-90">
            “{fragment}…”
          </div>
          <div className="mt-4 text-[10px] uppercase tracking-[0.4em] opacity-60">
            Moneyssance Journal
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Post renderer ---------------- */
export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    api
      .get(`/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Not found"));
  }, [slug]);

  const blocks = useMemo(() => (post ? parseContent(post.content) : []), [post]);
  const toc = useMemo(() => blocks.filter((b) => b.type === "h2"), [blocks]);

  // Track active heading via IntersectionObserver
  useEffect(() => {
    if (!toc.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveId(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    toc.forEach((h) => {
      const el = document.getElementById(h.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [toc]);

  if (error) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="post-error">
        <div className="text-xs uppercase tracking-widest text-foreground/50 mb-4">{error}</div>
        <Link to="/blog" className="text-primary underline">Back to the journal</Link>
      </main>
    );
  }

  if (!post) return <main className="max-w-3xl mx-auto px-6 py-32 text-foreground/60" data-testid="post-loading">Loading…</main>;

  const date = new Date(post.created_at).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });

  // Find index of first paragraph block for drop cap
  const firstParaIdx = blocks.findIndex((b) => b.type === "p");

  return (
    <main data-testid={`post-page-${post.slug}`} className="pb-24">
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
      />
      {/* Header */}
      <div className="max-w-4xl mx-auto px-6 md:px-10 pt-14 md:pt-20">
        <Link
          to="/blog"
          data-testid="post-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-foreground/60 hover:text-primary transition-colors mb-10"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to the journal
        </Link>
        <div className="text-[10px] uppercase tracking-[0.3em] text-accent">
          {post.category} · {post.read_minutes} min read · {date}
        </div>
        <h1 className="mt-6 font-serif text-4xl md:text-6xl font-light leading-[1.05] tracking-tighter">
          {post.title}
        </h1>
        <p className="mt-8 text-xl md:text-2xl text-foreground/70 leading-relaxed max-w-2xl font-serif italic">
          {post.excerpt}
        </p>
      </div>

      {/* Banner — real image or typographic fallback */}
      <div className="max-w-5xl mx-auto px-6 md:px-10 mt-14">
        {post.cover_image ? (
          <div className="aspect-[16/9] overflow-hidden rounded-sm border border-border">
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        ) : (
          <TypographicBanner post={post} />
        )}
      </div>

      {/* Body with optional TOC */}
      <div className="max-w-6xl mx-auto px-6 md:px-10 mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* TOC — sticky on desktop when there are ≥2 sections */}
        {toc.length >= 2 && (
          <aside className="hidden lg:block lg:col-span-3">
            <nav className="sticky top-28" data-testid="post-toc">
              <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/50 mb-4">
                Sections
              </div>
              <ul className="space-y-3 text-sm border-l border-border">
                {toc.map((h) => (
                  <li key={h.id}>
                    <a
                      href={`#${h.id}`}
                      data-testid={`toc-${h.id}`}
                      className={`block pl-4 -ml-px border-l-2 transition-colors duration-200 leading-snug ${
                        activeId === h.id
                          ? "border-primary text-primary"
                          : "border-transparent text-foreground/60 hover:text-primary"
                      }`}
                    >
                      {h.text}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>
        )}

        <article
          className={`${toc.length >= 2 ? "lg:col-span-9" : "lg:col-span-12 max-w-3xl mx-auto"}`}
          data-testid="post-body"
        >
          {blocks.map((b, i) => {
            const blockKey = `${b.type}-${i}`;
            if (b.type === "h2")
              return (
                <h2
                  key={blockKey}
                  id={b.id}
                  className="font-serif text-3xl md:text-4xl tracking-tight leading-tight mt-16 mb-6 scroll-mt-24"
                >
                  <span className="text-accent mr-3 text-xl align-middle">§</span>
                  {b.text}
                </h2>
              );
            if (b.type === "h3")
              return (
                <h3 key={blockKey} className="font-serif text-2xl md:text-3xl tracking-tight leading-tight mt-10 mb-4">
                  {b.text}
                </h3>
              );
            if (b.type === "quote")
              return (
                <blockquote
                  key={blockKey}
                  className="my-8 border-l-2 border-primary pl-6 md:pl-8 font-serif italic text-xl md:text-2xl leading-snug text-foreground/85"
                >
                  {renderInline(b.text)}
                </blockquote>
              );
            if (b.type === "list")
              return (
                <ul key={blockKey} className="my-6 space-y-3 pl-2">
                  {b.items.map((item, j) => (
                    <li key={`${blockKey}-item-${j}`} className="flex gap-4 text-lg leading-[1.7] text-foreground/85">
                      <span className="text-accent mt-2 flex-shrink-0 h-1.5 w-1.5 rounded-full bg-accent" />
                      <span>{renderInline(item)}</span>
                    </li>
                  ))}
                </ul>
              );
            // Paragraph — drop cap on first paragraph only
            const isFirst = i === firstParaIdx;
            return (
              <p
                key={blockKey}
                className={`text-lg md:text-xl leading-[1.85] text-foreground/85 mb-6 ${
                  isFirst ? "first-paragraph" : ""
                }`}
              >
                {isFirst && (
                  <span className="float-left font-serif text-6xl md:text-7xl leading-[0.85] pr-3 pt-1 text-primary font-medium">
                    {b.text.charAt(0)}
                  </span>
                )}
                {isFirst ? renderInline(b.text.slice(1)) : renderInline(b.text)}
              </p>
            );
          })}
        </article>
      </div>

      {/* Footer */}
      <div className="max-w-3xl mx-auto px-6 md:px-10 mt-24 border-t border-border pt-10 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-foreground/50">Written by</div>
          <div className="font-serif text-2xl mt-1">{post.author_name || "Nicola"}</div>
        </div>
        <div className="flex items-center gap-6">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            data-testid="post-scroll-top"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-foreground/60 hover:text-primary transition-colors"
          >
            <ArrowUp size={14} strokeWidth={1.5} /> Top
          </button>
          <Link
            to="/blog"
            className="text-xs uppercase tracking-widest text-primary hover:opacity-80 transition-opacity"
          >
            More essays →
          </Link>
        </div>
      </div>
    </main>
  );
}
